import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from "react";

export const POPUP_SIZES = {
  small: { w: 380, h: 640, label: "Compatto (380×640)" },
  medium: { w: 460, h: 760, label: "Medio (460×760)" },
  large: { w: 620, h: 860, label: "Grande (620×860)" },
};

const CHANNELS = {
  whatsapp: { url: "https://web.whatsapp.com/", name: "crm-wa-web", label: "WhatsApp Web" },
  telegram: { url: "https://web.telegram.org/k/", name: "crm-tg-web", label: "Telegram Web" },
};

const ChannelsContext = createContext(null);

export const useChannels = () => {
  const ctx = useContext(ChannelsContext);
  if (!ctx) throw new Error("useChannels must be used within ChannelsProvider");
  return ctx;
};

export function ChannelsProvider({ children }) {
  // Window references survive route changes because this provider lives in Layout (never unmounts).
  const winRefs = useRef({ whatsapp: null, telegram: null });
  const [openState, setOpenState] = useState({ whatsapp: false, telegram: false });
  // Channels the user pinned to the CRM (persisted across full reloads).
  const [pinned, setPinned] = useState(() => ({
    whatsapp: localStorage.getItem("channel_pinned_whatsapp") === "1",
    telegram: localStorage.getItem("channel_pinned_telegram") === "1",
  }));
  const [popupSize, setPopupSizeState] = useState(() => localStorage.getItem("wa_popup_size") || "medium");

  const setPopupSize = useCallback((size) => {
    setPopupSizeState(size);
    localStorage.setItem("wa_popup_size", size);
  }, []);

  const isOpen = useCallback((key) => {
    const w = winRefs.current[key];
    return !!(w && !w.closed);
  }, []);

  const openChannel = useCallback((key) => {
    const cfg = CHANNELS[key];
    if (!cfg) return false;
    // 1) If we still hold a LIVE reference to the window, just focus it — never reopen/reload.
    //    This is what restores a minimized window (works for Telegram).
    const existing = winRefs.current[key];
    if (existing && !existing.closed) {
      try { existing.focus(); } catch { /* ignore */ }
      setOpenState((s) => ({ ...s, [key]: true }));
      return true;
    }
    // 2) No live reference (never opened, genuinely closed, or — for WhatsApp — the reference was
    //    severed by its Cross-Origin-Opener-Policy). Open/target the named window.
    const { w, h } = POPUP_SIZES[popupSize] || POPUP_SIZES.medium;
    const win = window.open("", cfg.name);
    if (!win) return false; // blocked by popup blocker
    let isBlank = false;
    try {
      isBlank = win.location.href === "about:blank";
    } catch {
      isBlank = false; // cross-origin => the channel is already loaded, just restore/focus it
    }
    if (isBlank) {
      try { win.resizeTo(w, h); } catch { /* ignore */ }
      try {
        const left = Math.max(0, window.screen.availWidth - w - 24);
        const top = Math.max(0, (window.screen.availHeight - h) / 2);
        win.moveTo(left, top);
      } catch { /* ignore */ }
      win.location.href = cfg.url;
    }
    try { win.focus(); } catch { /* ignore */ }
    winRefs.current[key] = win;
    localStorage.setItem(`channel_pinned_${key}`, "1");
    setPinned((p) => ({ ...p, [key]: true }));
    setOpenState((s) => ({ ...s, [key]: true }));
    return true;
  }, [popupSize]);

  const closeChannel = useCallback((key) => {
    const w = winRefs.current[key];
    if (w && !w.closed) { try { w.close(); } catch { /* ignore */ } }
    winRefs.current[key] = null;
    localStorage.removeItem(`channel_pinned_${key}`);
    setPinned((p) => ({ ...p, [key]: false }));
    setOpenState((s) => ({ ...s, [key]: false }));
  }, []);

  // Keep openState in sync with the actual OS window status (user may close it manually).
  useEffect(() => {
    const id = setInterval(() => {
      const next = { whatsapp: isOpen("whatsapp"), telegram: isOpen("telegram") };
      setOpenState((s) => (s.whatsapp === next.whatsapp && s.telegram === next.telegram ? s : next));
    }, 1500);
    return () => clearInterval(id);
  }, [isOpen]);

  const value = {
    channels: CHANNELS,
    openChannel,
    closeChannel,
    isOpen,
    openState,
    pinned,
    popupSize,
    setPopupSize,
  };
  return <ChannelsContext.Provider value={value}>{children}</ChannelsContext.Provider>;
}
